/**
 * 新規連携のためのプロバイダーカード (Gmail / Outlook 等).
 *
 * 体裁は /settings/tenant の Slack 連携ボタンと統一:
 * - ボタン内にブランド SVG アイコン
 * - Gmail は 4 辺別色枠線 (Google 四色), 他プロバイダーは brandColor 単色
 */

import { Loading } from "components/ui/Loading/Loading";
import type React from "react";
import type { CSSProperties } from "react";
import ProviderIcon from "routes/profile/components/ProviderIcon";

type Props = {
  provider: string;
  title: string;
  description: string;
  /** ブランド公式カラー (#EA4335 / #0078D4 等). 単色枠線時に使う. */
  brandColor: string;
  disabled?: boolean;
  comingSoonLabel?: string;
  onClick?: (provider: string) => void;
  loading?: boolean;
};

/**
 * 4 辺別色 (Gmail = Google 四色). border-*-color を使うと
 * 丸角コーナーで隣接 2 色が自動ブレンドされ、ボタンの縦横比にも依存しない.
 */
const GMAIL_BORDER_STYLE: CSSProperties = {
  borderTopColor: "#EA4335",
  borderRightColor: "#FBBC04",
  borderBottomColor: "#34A853",
  borderLeftColor: "#4285F4",
};

/**
 * プロバイダー別のアクティブ枠線スタイル.
 * @param {string} provider プロバイダー識別子
 * @param {string} brandColor ブランド単色
 * @return {CSSProperties} インライン style
 */
const borderStyle = (provider: string, brandColor: string): CSSProperties =>
  provider === "gmail" ? GMAIL_BORDER_STYLE : { borderColor: brandColor };

/**
 * プロバイダーを新規連携するためのカード.
 * @param {Props} props プロパティ
 * @return {JSX.Element} カード
 */
const EmailProviderOptionCard: React.FC<Props> = ({
  provider,
  title,
  description,
  brandColor,
  disabled,
  comingSoonLabel,
  onClick,
  loading,
}) => (
  <div className="border border-gray-200 rounded-lg p-4 bg-white">
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="font-medium mb-0.5">{title}</div>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <div className="shrink-0">
        {disabled ? (
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-solid border-gray-200"
            disabled
          >
            <ProviderIcon provider={provider} size={18} />
            {comingSoonLabel ?? "近日対応"}
          </button>
        ) : (
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-white hover:bg-gray-50 text-primary border-2 border-solid transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={borderStyle(provider, brandColor)}
            onClick={() => onClick?.(provider)}
            disabled={loading}
          >
            {loading ? (
              <Loading size="xs" />
            ) : (
              <ProviderIcon provider={provider} size={18} />
            )}
            {loading ? "接続準備中..." : `${title}と連携する`}
          </button>
        )}
      </div>
    </div>
  </div>
);

export default EmailProviderOptionCard;
